import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, getToken, clearToken, assetUrl } from "../lib/api";
import { Send, AlertTriangle, ArrowLeft, Search, Hash, MapPin, LogIn, LogOut, MessagesSquare, Sparkles, MessageCircle } from "lucide-react";
import { toast } from "sonner";

// ─── Utils ──────────────────────────────────────────────────────────
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T") + (iso.includes("T") ? "" : "Z"));
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const Avatar = ({ name, url, size = 36 }) => {
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return url ? (
    <img src={assetUrl(url)} alt={name} className="rounded-full object-cover shrink-0 border border-white/10" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full shrink-0 bg-yellow-400 text-black font-black flex items-center justify-center" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {initials}
    </div>
  );
};

// ─── Page principale ───────────────────────────────────────────────
export default function ArnakChatPage({ onBack }) {
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null); // slug
  const [me, setMe] = useState(null);
  const [authed, setAuthed] = useState(!!getToken());
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [unreadDm, setUnreadDm] = useState(0);
  const isArnakDomain = typeof window !== "undefined" && /arnakclan\.com$/i.test(window.location.hostname);

  // Poll les messages privés non lus toutes les 10s
  useEffect(() => {
    if (!authed) return;
    const fetchUnread = () => {
      api.get("/arnak/dm/unread-count").then(({ data }) => setUnreadDm(data.unread || 0)).catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 10000);
    return () => clearInterval(id);
  }, [authed]);

  // Load channels + me
  useEffect(() => {
    let mounted = true;
    api.get("/arnak/chat/channels").then(({ data }) => {
      if (!mounted) return;
      setChannels(data || []);
      // Activer "general" par défaut
      const def = (data || []).find((c) => c.slug === "general") || data?.[0];
      if (def) setActive(def.slug);
    }).catch(() => {}).finally(() => setLoading(false));
    if (authed) {
      api.get("/arnak/chat/me").then(({ data }) => mounted && setMe(data)).catch(() => setAuthed(false));
    }
    return () => { mounted = false; };
  }, [authed]);

  // Groupe les salons par kind
  const grouped = useMemo(() => {
    const filter = (c) => !query || c.name.toLowerCase().includes(query.toLowerCase());
    return {
      topic:    channels.filter((c) => c.kind === "topic" && filter(c)),
      city:     channels.filter((c) => c.kind === "city" && filter(c)),
      diaspora: channels.filter((c) => c.kind === "diaspora" && filter(c)),
    };
  }, [channels, query]);

  // Détecte si on est sur arnakclan.com (domaine satellite) ou sur voamada.com/arnak/chat
  const backHref = isArnakDomain ? "/" : "/arnak";

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-yellow-400/20 px-4 md:px-8 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Bouton retour vers la page d'accueil ARNAK — toujours visible */}
          <Link
            to={backHref}
            className="p-2 -ml-1 rounded-lg hover:bg-yellow-400/15 hover:text-yellow-400 text-white/85 transition shrink-0"
            title="Retour à arnakclan.com"
            data-testid="chat-back-home"
          >
            <ArrowLeft size={22} />
          </Link>
          <Link to={backHref} className="min-w-0 hover:opacity-90 transition" title="Retour à l'accueil">
            <p className="font-black text-xl md:text-2xl tracking-tight truncate" style={{ fontFamily: "'Anton', sans-serif" }}>
              <span className="text-yellow-400">ARNAK</span>CHAT 🇲🇬
            </p>
            <p className="text-[10px] uppercase tracking-widest text-white/55 hidden sm:block">← Retour sur arnakclan.com</p>
          </Link>
        </div>
        {me?.banned && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/40 text-[10px] uppercase tracking-widest font-bold">
            <AlertTriangle size={12} /> Banni
          </span>
        )}
        {authed && me && !me.banned && (
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-white/70 font-bold truncate max-w-[140px]" data-testid="chat-username">
              👤 {me.name || "Membre"}
            </span>
            <Link
              to={isArnakDomain ? "/messages" : "/arnak/messages"}
              className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-400/15 hover:bg-yellow-400/30 border border-yellow-400/40 text-yellow-300 text-[10px] uppercase tracking-widest font-bold transition"
              title="Mes messages privés"
              data-testid="chat-dm-link"
            >
              <MessageCircle size={12} /> <span className="hidden sm:inline">Messages</span>
              {unreadDm > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center">
                  {unreadDm > 9 ? "9+" : unreadDm}
                </span>
              )}
            </Link>
            <button
              onClick={() => { clearToken(); window.location.reload(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-[10px] uppercase tracking-widest font-bold transition"
              title="Se déconnecter"
              data-testid="chat-logout-btn"
            >
              <LogOut size={12} /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        )}
      </header>

      {/* Bandeau de bienvenue (collapsible) */}
      <WelcomeBanner />

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Sidebar salons (desktop uniquement) */}
        <aside className="hidden lg:flex w-72 xl:w-80 shrink-0 flex-col bg-zinc-950 border-r border-white/10">
          <ChannelList
            grouped={grouped}
            active={active}
            onSelect={setActive}
            query={query}
            onQuery={setQuery}
            loading={loading}
          />
        </aside>

        {/* Mobile : sélecteur de salon en tête, pleine largeur */}
        <div className="lg:hidden border-b border-white/10 bg-zinc-950 shrink-0">
          <MobileChannelPicker
            grouped={grouped}
            active={active}
            onSelect={setActive}
            query={query}
            onQuery={setQuery}
          />
        </div>

        {/* Zone messages */}
        <main className="flex-1 flex flex-col min-w-0">
          {!authed ? (
            <NotLoggedIn />
          ) : !active ? (
            <div className="flex-1 flex items-center justify-center text-white/40 p-8 text-center">
              <p>Sélectionne un salon pour commencer à discuter ✨</p>
            </div>
          ) : (
            <ChannelView
              channelSlug={active}
              me={me}
              channels={channels}
            />
          )}
        </main>
      </div>
    </div>
  );
}

// ─── Bannière de bienvenue ─────────────────────────────────────────
const WelcomeBanner = () => {
  const [open, setOpen] = useState(() => !localStorage.getItem("arnakchat_welcome_dismissed_v1"));
  if (!open) return null;
  return (
    <div className="relative bg-gradient-to-r from-yellow-400/15 via-yellow-400/8 to-transparent border-b border-yellow-400/20 px-4 md:px-8 py-3.5 text-xs md:text-sm">
      <button
        onClick={() => { setOpen(false); localStorage.setItem("arnakchat_welcome_dismissed_v1", "1"); }}
        className="absolute top-1.5 right-2 p-1 text-white/50 hover:text-white text-xs font-bold"
        aria-label="Fermer"
      >
        ✕
      </button>
      <p className="text-yellow-300 font-bold mb-1">🔥 Bienvenue sur ArnakChat 🇲🇬</p>
      <p className="text-white/80 leading-relaxed">
        Discute, partage, rencontre du monde, trouve du travail, du covoiturage, de la musique, des bons plans et entraide entre gasy ❤️ ·
        <span className="text-yellow-400 font-bold"> ✅ Respect</span> ·
        <span className="text-red-400 font-bold"> ❌ Insultes, arnaques, spam</span>
      </p>
    </div>
  );
};

// ─── Sidebar desktop ────────────────────────────────────────────────
const ChannelList = ({ grouped, active, onSelect, query, onQuery, loading }) => (
  <>
    <div className="p-3 border-b border-white/10">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Rechercher un salon…"
          className="w-full bg-white/5 border border-white/10 focus:border-yellow-400/50 outline-none pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/40 rounded"
          data-testid="chat-search"
        />
      </div>
    </div>
    <div className="flex-1 overflow-y-auto py-2">
      {loading ? <p className="p-4 text-xs text-white/40 text-center">Chargement…</p> : (
        <>
          <ChannelGroup label="Rubriques" Icon={Hash} items={grouped.topic} active={active} onSelect={onSelect} />
          <ChannelGroup label="🇲🇬 Salons par ville" Icon={MapPin} items={grouped.city} active={active} onSelect={onSelect} />
          <ChannelGroup label="🌍 Diaspora gasy" Icon={Sparkles} items={grouped.diaspora} active={active} onSelect={onSelect} />
        </>
      )}
    </div>
  </>
);

const ChannelGroup = ({ label, Icon, items, active, onSelect }) => {
  if (!items?.length) return null;
  return (
    <div className="px-2 pb-3">
      <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest font-bold text-white/45">{label}</p>
      <ul className="space-y-0.5">
        {items.map((c) => (
          <li key={c.channel_id}>
            <button
              onClick={() => onSelect(c.slug)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded text-sm text-left transition ${active === c.slug ? "bg-yellow-400/15 text-yellow-300 font-bold" : "text-white/75 hover:bg-white/5 hover:text-white"}`}
              data-testid={`chat-channel-${c.slug}`}
            >
              <span className="text-base">{c.emoji || "#"}</span>
              <span className="flex-1 truncate">{c.name}</span>
              {c.messages_24h > 0 && <span className="text-[9px] bg-white/10 text-white/60 px-1.5 rounded-full">{c.messages_24h}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Mobile picker (select stylé) ──────────────────────────────────
const MobileChannelPicker = ({ grouped, active, onSelect, query, onQuery }) => {
  const [open, setOpen] = useState(false);
  const current = [...grouped.topic, ...grouped.city, ...grouped.diaspora].find((c) => c.slug === active);
  return (
    <div className="px-4 py-2.5">
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded text-sm font-bold"
        data-testid="mobile-channel-picker"
      >
        <span className="flex items-center gap-2 truncate">
          <span className="text-lg">{current?.emoji || "💬"}</span>
          <span className="truncate">{current?.name || "Choisir un salon…"}</span>
        </span>
        <span className="text-yellow-400 text-xs">CHANGER ▾</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur flex flex-col" onClick={() => setOpen(false)}>
          <div className="bg-zinc-950 border-b border-yellow-400/30 p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-black text-lg">Choisir un salon</p>
              <button onClick={() => setOpen(false)} className="text-white/60 text-2xl">✕</button>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="search"
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                placeholder="Rechercher…"
                className="w-full bg-white/5 border border-white/10 outline-none pl-9 pr-3 py-2.5 text-sm rounded"
                autoFocus
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-zinc-950" onClick={(e) => e.stopPropagation()}>
            <ChannelGroup label="Rubriques" Icon={Hash} items={grouped.topic} active={active} onSelect={(s) => { onSelect(s); setOpen(false); }} />
            <ChannelGroup label="🇲🇬 Salons par ville" Icon={MapPin} items={grouped.city} active={active} onSelect={(s) => { onSelect(s); setOpen(false); }} />
            <ChannelGroup label="🌍 Diaspora gasy" Icon={Sparkles} items={grouped.diaspora} active={active} onSelect={(s) => { onSelect(s); setOpen(false); }} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Pas connecté ──────────────────────────────────────────────────
const NotLoggedIn = () => {
  // Reste sur le domaine courant (ne renvoie PAS vers voamada.com).
  // Une fois loggé, on revient sur /chat automatiquement.
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-4">
      <MessagesSquare size={56} className="text-yellow-400/60" />
      <h2 className="font-black text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
        Connecte-toi pour rejoindre la discussion
      </h2>
      <p className="text-white/60 max-w-md text-sm">
        ArnakChat est réservé aux membres pour garantir une communauté saine.
        C'est gratuit, ça prend 30 secondes.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
        <a
          href="/login?redirect=/chat"
          className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm transition"
          data-testid="chat-login-btn"
        >
          <LogIn size={14} /> Se connecter
        </a>
        <a
          href="/login?redirect=/chat&tab=signup"
          className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-black uppercase tracking-widest text-sm transition"
          data-testid="chat-signup-btn"
        >
          Créer un compte
        </a>
      </div>
    </div>
  );
};

// ─── Vue d'un salon (messages + input) ─────────────────────────────
const ChannelView = ({ channelSlug, me, channels }) => {
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const lastIdRef = useRef(null);
  const isArnakDomain = typeof window !== "undefined" && /arnakclan\.com$/i.test(window.location.hostname);

  // Initial load
  useEffect(() => {
    setLoading(true);
    setMessages([]);
    lastIdRef.current = null;
    api.get(`/arnak/chat/messages?channel=${encodeURIComponent(channelSlug)}`)
      .then(({ data }) => {
        setChannel(data.channel);
        setMessages(data.messages || []);
        if (data.messages?.length) lastIdRef.current = data.messages[data.messages.length - 1].message_id;
      })
      .catch((e) => {
        if (e?.response?.status === 401) toast.error("Connecte-toi pour lire les messages");
        else toast.error("Erreur de chargement");
      })
      .finally(() => setLoading(false));
  }, [channelSlug]);

  // Poll for new messages every 4s
  useEffect(() => {
    if (!channelSlug || !channel) return;
    const t = setInterval(async () => {
      if (!lastIdRef.current) return;
      try {
        const { data } = await api.get(`/arnak/chat/messages?channel=${encodeURIComponent(channelSlug)}&after=${lastIdRef.current}`);
        if (data.messages?.length) {
          setMessages((prev) => [...prev, ...data.messages]);
          lastIdRef.current = data.messages[data.messages.length - 1].message_id;
        }
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [channelSlug, channel]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/arnak/chat/messages", { channel: channelSlug, body: text });
      if (data?.message) {
        setMessages((prev) => [...prev, data.message]);
        lastIdRef.current = data.message.message_id;
      }
      setInput("");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Impossible d'envoyer");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {channel && (
        <div className="hidden lg:block px-6 py-3 border-b border-white/10 bg-black/50">
          <p className="font-black text-base flex items-center gap-2">
            <span className="text-xl">{channel.emoji || "#"}</span> {channel.name}
          </p>
          {channel.description && <p className="text-xs text-white/55 mt-0.5">{channel.description}</p>}
        </div>
      )}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 md:px-6 py-4 space-y-2.5">
        {loading ? (
          <p className="text-center text-white/40 text-sm py-8">Chargement des messages…</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 px-4">
            <p className="text-white/60 text-sm">Sois le premier à écrire dans ce salon ✨</p>
          </div>
        ) : (
          messages.map((m, i) => {
            const prev = messages[i - 1];
            const sameAuthor = prev && prev.user_id === m.user_id && (new Date(m.created_at) - new Date(prev.created_at)) < 5 * 60 * 1000;
            const isMine = me && m.user_id === me.user_id;
            return (
              <MessageBubble
                key={m.message_id}
                msg={m}
                sameAuthor={sameAuthor}
                isMine={isMine}
                currentUserId={me?.user_id}
                isArnakDomain={isArnakDomain}
              />
            );
          })
        )}
      </div>

      <form onSubmit={send} className="border-t border-white/10 p-3 md:p-4 bg-zinc-950">
        {me?.banned ? (
          <p className="text-center text-red-400 text-sm">Tu es banni du chat. Contacte un administrateur.</p>
        ) : (
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Écrire dans #${channel?.name || channelSlug}…`}
              rows={1}
              maxLength={2000}
              className="flex-1 resize-none bg-white/5 border border-white/10 focus:border-yellow-400/60 outline-none px-3 py-2.5 text-sm rounded leading-snug"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="shrink-0 px-4 py-2.5 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black rounded inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest transition"
              data-testid="chat-send"
            >
              <Send size={13} /> Envoyer
            </button>
          </div>
        )}
        <p className="text-[10px] text-white/40 mt-1.5">Entrée pour envoyer · Shift+Entrée pour aller à la ligne · respect ❤️</p>
      </form>
    </>
  );
};

const MessageBubble = ({ msg, sameAuthor, isMine, currentUserId, isArnakDomain }) => {
  const canDm = !isMine && msg.user_id && msg.user_id !== currentUserId;
  const dmHref = (isArnakDomain ? "/messages" : "/arnak/messages") + "?with=" + encodeURIComponent(msg.user_id || "");
  return (
    <div className={`flex gap-2.5 ${isMine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${msg.message_id}`}>
      {!sameAuthor ? (
        canDm ? (
          <Link to={dmHref} title={`Envoyer un message privé à ${msg.user_name}`} className="hover:opacity-80 transition">
            <Avatar name={msg.user_name} url={msg.avatar_url} size={32} />
          </Link>
        ) : (
          <Avatar name={msg.user_name} url={msg.avatar_url} size={32} />
        )
      ) : (
        <div className="w-8 shrink-0" />
      )}
      <div className={`max-w-[78%] ${isMine ? "items-end" : "items-start"} flex flex-col`}>
        {!sameAuthor && (
          <p className="text-[11px] mb-0.5 flex items-center gap-2 flex-wrap">
            {canDm ? (
              <Link
                to={dmHref}
                className={`font-bold hover:underline ${isMine ? "text-yellow-300" : "text-yellow-400"}`}
                data-testid={`open-dm-${msg.user_id}`}
              >
                {msg.user_name || "Anonyme"}
              </Link>
            ) : (
              <span className={`font-bold ${isMine ? "text-yellow-300" : "text-yellow-400"}`}>{msg.user_name || "Anonyme"}</span>
            )}
            <span className="text-white/40">{fmtTime(msg.created_at)}</span>
            {canDm && (
              <Link to={dmHref} className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-[10px] text-white/45 hover:text-yellow-400 transition" title="Message privé">
                💬 MP
              </Link>
            )}
          </p>
        )}
        <div className={`group px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words rounded-2xl ${isMine ? "bg-yellow-400 text-black rounded-br-sm" : "bg-white/[0.07] text-white rounded-bl-sm"}`}>
          {linkify(msg.body)}
        </div>
      </div>
    </div>
  );
};

// ─── Linkify simple ────────────────────────────────────────────────
const URL_RE = /(https?:\/\/[^\s]+)/g;
function linkify(text) {
  if (!text) return null;
  const parts = text.split(URL_RE);
  return parts.map((p, i) => {
    if (URL_RE.test(p)) {
      URL_RE.lastIndex = 0;
      return (
        <a key={i} href={p} target="_blank" rel="noreferrer" className="underline hover:text-yellow-300 break-all">
          {p}
        </a>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}
